import { useState } from 'react';

import jwtDecode from 'jwt-decode';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import i18n from '@utils/i18n';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useIsMobile from '@hooks/useIsMobile';
import { JwtPayload } from '@interfaces';
import { authServices } from '@services/index';
import { setAuthenticated, setProfile, setRoles } from '@store/reducers/authReducer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@ui/alert-dialog';
import { cn } from '@ui/utils/cn';
import { AuthStorage } from '@utils/auth';
import error2Text from '@utils/error2Text';

import '../../../tailwind.css';
import { LeftPanel } from './LeftPanel';
import { LoginForm } from './LoginForm';

interface LoginFormData {
  username: string;
  password: string;
  remember?: boolean;
}

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [errorAlert, setErrorAlert] = useState<{ title: string; content?: string } | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation('translation', { keyPrefix: 'login' });
  const isMobile = useIsMobile();

  const handleLogin = async (data: LoginFormData) => {
    if (!data?.username) {
      setErrorAlert({ title: t('error.blank-username.title'), content: t('error.blank-username.content') });
    } else if (!data?.password) {
      setErrorAlert({ title: t('error.blank-password.title'), content: t('error.blank-password.content') });
    } else {
      setLoading(true);
      const { error: resError, data: resData } = await authServices.login(data);

      if (resError) {
        setErrorAlert(error2Text(resError));
      } else if (resData) {
        const { name, sub, scope } = jwtDecode<JwtPayload>(resData?.accessToken);

        // Redux 상태 업데이트
        const profileData = { name, id_admin: sub };
        dispatch(setProfile(profileData));
        dispatch(setRoles(scope));

        // AuthStorage를 사용하여 안전하게 저장 (refreshToken 먼저 저장)
        AuthStorage.setRefreshToken(resData?.refreshToken);
        AuthStorage.setToken(resData?.accessToken);
        AuthStorage.setProfile(profileData);
        AuthStorage.setRoles(scope);
        AuthStorage.setUserId(data.username);

        if (navigator.userAgent) {
          AuthStorage.setSessionID(resData?.sessionID);
        }

        // 로그인 상태 유지 처리
        if (data?.remember) {
          AuthStorage.setRememberedUsername(data.username);
        } else {
          AuthStorage.clearRememberedUsername();
        }

        dispatch(setAuthenticated(true));

        // search param에서 redirect URL 읽기
        const redirectUrl = searchParams.get('redirect');
        const targetPath = redirectUrl ? decodeURIComponent(redirectUrl) : '/';

        // SPA 방식으로 리다이렉트 (깜빡임 없음)
        navigate(targetPath, { replace: true });
      }
      setLoading(false);
    }
  };

  const errorDialog = (
    <AlertDialog open={!!errorAlert} onOpenChange={() => setErrorAlert(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{errorAlert?.title}</AlertDialogTitle>
          {errorAlert?.content && (
            <AlertDialogDescription className="whitespace-pre-line">
              {errorAlert.content}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction className="min-w-[80px]" onClick={() => setErrorAlert(null)}>
            {i18n.t('button.check')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (isMobile) {
    return (
      <>
        <Helmet>
          <title>{t('pageTitle')}</title>
        </Helmet>
        <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden px-[30px]">
          <LeftPanel isMobile />
          <div className="relative z-10 w-full max-w-[400px] animate-fade-in-up">
            <div className="rounded-lg bg-white p-[25px_18px]">
              <LoginForm handleLogin={handleLogin} loading={loading} isMobile />
            </div>
            <p className="mt-3 text-center text-[11px] text-white/60">
              Copyright &copy; Worker&apos;s High Co., Ltd. All Rights Reserved.
            </p>
          </div>
        </div>
        {errorDialog}
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('pageTitle')}</title>
      </Helmet>
      <div className="h-screen w-screen overflow-hidden">
        {/* 배경색 확장 레이어 */}
        <div className="absolute inset-0 flex">
          <div className="w-1/2 bg-[#252525]" />
          <div className="w-1/2 bg-white" />
        </div>

        {/* 콘텐츠 (max 1920px 중앙 고정) */}
        <div className="relative mx-auto flex h-full max-w-[1440px]">
          <LeftPanel isMobile={false} />
          <div className="relative flex w-1/2 items-center justify-center overflow-hidden bg-white">
            <div
              className={cn(
                'flex w-full flex-col',
                'px-6 py-16',
                'lg:px-8 lg:py-[150px]',
                'xl:px-10',
                'min-[1440px]:px-[60px]',
              )}
            >
              <LoginForm handleLogin={handleLogin} loading={loading} isMobile={false} />
            </div>
            <span className="absolute bottom-6 text-[11px] text-gray-400">
              Copyright &copy; Worker&apos;s High Co., Ltd. All Rights Reserved.
            </span>
          </div>
        </div>
      </div>
      {errorDialog}
    </>
  );
}
